import { fg } from '@atlaskit/platform-feature-flags/fg';
import type {
	SlidesAiEditActionCompletedCommandProperties,
	SlidesAiEditFirstWriteCommandProperties,
} from '@atlassian/rovo-content-bridge-api-commands/commands/slides-ai-edit-measurement-commands';

export type SlidesAiEditOutcome =
	| 'success'
	| 'no_edit'
	| 'failure'
	| 'cancelled'
	| 'unsupported_receiver'
	| 'unsupported_sender'
	| 'missing_acknowledgement'
	| 'invalid_measurement';

type Result = {
	outcome: SlidesAiEditOutcome;
	entrypoint: 'inline' | 'chat';
	protocolVersion: 1;
	protocolSupported: boolean;
	alwaysVisible: boolean;
	updateActionCount: number;
	affectedSlideCount: number;
	changedChildElementCount: number;
	firstAppliedDurationMs?: number;
	fullyAppliedDurationMs?: number;
	acknowledgementLagMs?: number;
};

type Action = {
	receiverId: string;
	supported: boolean;
	completion?: SlidesAiEditActionCompletedCommandProperties;
};
type Request = {
	submittedAt: number;
	entrypoint: 'inline' | 'chat';
	submissionReceiverIds: string[];
	eligible: boolean;
	senderSupported: boolean;
	inlineReceiverSupported?: boolean;
	invalid: boolean;
	failed: boolean;
	actions: Map<string, Action>;
	firstAppliedAt?: number;
	firstHiddenAt?: number;
	acknowledgementLagMs: number;
	terminal?: 'success' | 'failure' | 'cancelled';
	deadline: number;
	timer: ReturnType<typeof setTimeout>;
	emit: (result: Result) => void;
};

const REQUEST_TIMEOUT_MS = 300_000;
const ACK_TIMEOUT_MS = 30_000;
const MAX_REQUESTS = 100;
// Allow up to 2 ms of observed cross-context browser precision inversion.
// Keep larger ordering errors invalid rather than hiding them in successful latency.
const TIMESTAMP_PRECISION_MS = 2;
// Communicating browser contexts share a monotonic epoch; their relative time origins can differ.
// https://www.w3.org/TR/hr-time-3/#sec-time-origin
const clock = (): number => performance.timeOrigin + performance.now();

/** Keeps bridge identities in memory only; emitted results contain aggregate measurements. */
export class SlidesAiEditLatencyTracker {
	private requests = new Map<string, Request>();
	private targets = new Map<string, { contentId: string; supportsMeasurement?: true }>();
	private visibility: { at: number; hidden: boolean }[] = [];
	private observing = false;

	constructor(
		private readonly now: () => number = clock,
		private readonly doc: Document | undefined = typeof document === 'undefined'
			? undefined
			: document,
	) {}

	private observeVisibility = (): void => {
		const at = this.now();
		const hidden = this.doc?.visibilityState === 'hidden';
		this.visibility.push({ at, hidden });
		// Retain the state immediately before the oldest possible live request.
		while (this.visibility.length > 1 && this.visibility[1].at < at - REQUEST_TIMEOUT_MS) {
			this.visibility.shift();
		}
		if (hidden) {
			for (const request of this.requests.values()) {
				request.firstHiddenAt ??= at;
			}
		}
	};

	mount(receiverId: string, contentId: string, supportsMeasurement?: true): void {
		if (!fg('rovo_slides_ai_edit_latency')) {
			return;
		}
		this.targets.set(receiverId, { contentId, supportsMeasurement });
		if (!this.observing) {
			this.observing = true;
			this.observeVisibility();
			this.doc?.addEventListener('visibilitychange', this.observeVisibility);
		}
	}

	unmount(receiverId: string): void {
		this.targets.delete(receiverId);
		for (const [id, request] of this.requests) {
			if (
				request.submissionReceiverIds.includes(receiverId) ||
				[...request.actions.values()].some((action) => action.receiverId === receiverId)
			) {
				this.finish(id, 'cancelled');
			}
		}
	}

	begin({
		editRequestId,
		editSubmittedAt,
		inline,
		contentId,
		emit,
	}: {
		editRequestId?: string;
		editSubmittedAt?: number;
		inline: boolean;
		contentId?: string | number;
		emit: (result: Result) => void;
	}): string | undefined {
		if (!fg('rovo_slides_ai_edit_latency')) {
			return undefined;
		}
		const id = editRequestId || crypto.randomUUID();
		if (this.requests.has(id)) {
			return id;
		}
		if (this.requests.size >= MAX_REQUESTS) {
			const oldest = this.requests.keys().next().value;
			if (oldest) {
				this.finish(oldest, 'missing_acknowledgement');
			}
		}
		const receivedAt = this.now();
		const submittedAt = editSubmittedAt ?? receivedAt;
		const submissionTargets = [...this.targets].filter(
			([, target]) => contentId !== undefined && target.contentId === String(contentId),
		);
		const firstHiddenAt =
			this.visibility.reduce<number | undefined>((first, change, index) => {
				const next = this.visibility[index + 1];
				return (
					first ??
					(change.hidden && (!next || next.at > submittedAt)
						? Math.max(change.at, submittedAt)
						: undefined)
				);
			}, undefined) ?? (this.doc?.visibilityState === 'hidden' ? submittedAt : undefined);
		this.requests.set(id, {
			submittedAt,
			entrypoint: inline ? 'inline' : 'chat',
			submissionReceiverIds: submissionTargets.map(([receiverId]) => receiverId),
			eligible: inline,
			inlineReceiverSupported: inline
				? submissionTargets.some(([, target]) => target.supportsMeasurement === true)
				: undefined,
			senderSupported: !inline || Boolean(editRequestId && editSubmittedAt !== undefined),
			invalid:
				!Number.isFinite(submittedAt) ||
				submittedAt - receivedAt > TIMESTAMP_PRECISION_MS ||
				receivedAt - submittedAt > REQUEST_TIMEOUT_MS,
			failed: false,
			actions: new Map(),
			firstHiddenAt,
			acknowledgementLagMs: 0,
			deadline: receivedAt + REQUEST_TIMEOUT_MS,
			timer: setTimeout(() => this.finish(id, 'missing_acknowledgement'), REQUEST_TIMEOUT_MS),
			emit,
		});
		return id;
	}

	classify(id: string | undefined, kind: 'update' | 'create'): void {
		const request = id ? this.requests.get(id) : undefined;
		if (!request || !id) {
			return;
		}
		if (kind === 'create') {
			clearTimeout(request.timer);
			this.requests.delete(id);
		} else {
			request.eligible = true;
		}
	}

	deliver(id: string | undefined, actionId: string | undefined, receiverId: string): void {
		const request = id ? this.requests.get(id) : undefined;
		if (!request) {
			return;
		}
		request.eligible = true;
		if (!actionId) {
			request.invalid = true;
			return;
		}
		if (!request.actions.has(actionId)) {
			request.actions.set(actionId, {
				receiverId,
				supported: this.targets.get(receiverId)?.supportsMeasurement === true,
			});
		}
	}

	firstWrite(command: SlidesAiEditFirstWriteCommandProperties): void {
		const request = this.requests.get(command.editRequestId);
		const action = request?.actions.get(command.editActionId);
		if (!request || !action?.supported || action.receiverId !== command.senderId) {
			return;
		}
		if (
			!Number.isFinite(command.firstAppliedAt) ||
			request.submittedAt - command.firstAppliedAt > TIMESTAMP_PRECISION_MS ||
			command.firstAppliedAt - this.now() > TIMESTAMP_PRECISION_MS
		) {
			request.invalid = true;
			return;
		}
		request.firstAppliedAt = Math.min(request.firstAppliedAt ?? Infinity, command.firstAppliedAt);
		request.acknowledgementLagMs = Math.max(
			request.acknowledgementLagMs,
			this.now() - command.firstAppliedAt,
		);
	}

	complete(command: SlidesAiEditActionCompletedCommandProperties): void {
		const request = this.requests.get(command.editRequestId);
		const action = request?.actions.get(command.editActionId);
		if (
			!request ||
			!action?.supported ||
			action.receiverId !== command.senderId ||
			action.completion
		) {
			return;
		}
		const validCounts = [command.affectedSlideCount, command.changedChildElementCount].every(
			(count) => Number.isSafeInteger(count) && count >= 0,
		);
		if (
			!['success', 'failure', 'cancelled'].includes(command.status) ||
			!validCounts ||
			!Number.isFinite(command.completedAt) ||
			request.submittedAt - command.completedAt > TIMESTAMP_PRECISION_MS ||
			command.completedAt - this.now() > TIMESTAMP_PRECISION_MS ||
			(command.firstAppliedAt !== undefined &&
				command.firstAppliedAt - command.completedAt > TIMESTAMP_PRECISION_MS)
		) {
			request.invalid = true;
		}
		if (command.firstAppliedAt !== undefined) {
			this.firstWrite({ ...command, firstAppliedAt: command.firstAppliedAt });
		}
		action.completion = command;
		request.acknowledgementLagMs = Math.max(
			request.acknowledgementLagMs,
			this.now() - command.completedAt,
		);
		this.maybeFinish(command.editRequestId);
	}

	markFailed(id: string | undefined): void {
		const request = id ? this.requests.get(id) : undefined;
		if (request) {
			request.failed = true;
		}
	}

	end(id: string | undefined, outcome: 'success' | 'failure' | 'cancelled'): void {
		const request = id ? this.requests.get(id) : undefined;
		if (!request || !id) {
			return;
		}
		request.terminal =
			request.terminal === 'failure' || request.terminal === 'cancelled'
				? request.terminal
				: outcome;
		clearTimeout(request.timer);
		const now = this.now();
		request.deadline = Math.min(request.deadline, now + ACK_TIMEOUT_MS);
		if (request.deadline <= now) {
			this.finish(id, 'missing_acknowledgement');
			return;
		}
		request.timer = setTimeout(
			() => this.finish(id, 'missing_acknowledgement'),
			request.deadline - now,
		);
		this.maybeFinish(id);
	}

	private maybeFinish(id: string): void {
		const request = this.requests.get(id);
		if (!request?.terminal) {
			return;
		}
		if ([...request.actions.values()].every((action) => !action.supported || action.completion)) {
			this.finish(id, request.terminal);
		}
	}

	private finish(id: string, fallback: SlidesAiEditOutcome): void {
		const request = this.requests.get(id);
		if (!request) {
			return;
		}
		this.requests.delete(id);
		clearTimeout(request.timer);
		if (!request.eligible) {
			return;
		}
		const actions = [...request.actions.values()];
		const supported =
			request.inlineReceiverSupported !== false && actions.every((action) => action.supported);
		const completions = actions.flatMap((action) => (action.completion ? [action.completion] : []));
		// Canvas snapshots count distinct changes across all actions in this request, per receiver.
		// Take each receiver's maximum because an earlier snapshot can arrive after a later one.
		const counts = new Map<string, { slides: number; children: number }>();
		for (const completion of completions) {
			const previous = counts.get(completion.senderId);
			counts.set(completion.senderId, {
				slides: Math.max(previous?.slides ?? 0, completion.affectedSlideCount),
				children: Math.max(previous?.children ?? 0, completion.changedChildElementCount),
			});
		}
		const affectedSlideCount = [...counts.values()].reduce(
			(total, count) => total + count.slides,
			0,
		);
		const changedChildElementCount = [...counts.values()].reduce(
			(total, count) => total + count.children,
			0,
		);
		const completedAt = completions.length
			? Math.max(...completions.map((completion) => completion.completedAt))
			: undefined;
		let outcome: SlidesAiEditOutcome = fallback;
		if (
			request.failed ||
			request.terminal === 'failure' ||
			completions.some((completion) => completion.status === 'failure')
		) {
			outcome = 'failure';
		} else if (
			request.terminal === 'cancelled' ||
			completions.some((completion) => completion.status === 'cancelled')
		) {
			outcome = 'cancelled';
		} else if (!request.senderSupported) {
			outcome = 'unsupported_sender';
		} else if (!supported) {
			outcome = 'unsupported_receiver';
		} else if (
			request.invalid ||
			(affectedSlideCount > 0 && request.firstAppliedAt === undefined) ||
			(request.firstAppliedAt !== undefined &&
				affectedSlideCount === 0 &&
				completions.length === actions.length) ||
			(completedAt !== undefined &&
				request.firstAppliedAt !== undefined &&
				request.firstAppliedAt - completedAt > TIMESTAMP_PRECISION_MS)
		) {
			outcome = 'invalid_measurement';
		} else if (fallback === 'success') {
			outcome = request.firstAppliedAt === undefined ? 'no_edit' : 'success';
		}
		const firstAppliedAt = request.firstAppliedAt;
		const validFirst = request.senderSupported && !request.invalid && firstAppliedAt !== undefined;
		const validFull = outcome === 'success' && completedAt !== undefined;
		// Normalize only after validating the precision bound, keeping first <= full and durations >= 0.
		const firstAppliedDurationMs = validFirst
			? Math.max(0, firstAppliedAt - request.submittedAt)
			: undefined;
		const fullyAppliedDurationMs = validFull
			? Math.max(firstAppliedDurationMs ?? 0, completedAt - request.submittedAt)
			: undefined;
		const endpoint =
			fullyAppliedDurationMs !== undefined
				? request.submittedAt + fullyAppliedDurationMs
				: this.now();
		request.emit({
			outcome,
			entrypoint: request.entrypoint,
			protocolVersion: 1,
			protocolSupported: supported && request.senderSupported,
			alwaysVisible: request.firstHiddenAt === undefined || request.firstHiddenAt > endpoint,
			updateActionCount: actions.length,
			affectedSlideCount: Number.isSafeInteger(affectedSlideCount) ? affectedSlideCount : 0,
			changedChildElementCount: Number.isSafeInteger(changedChildElementCount)
				? changedChildElementCount
				: 0,
			firstAppliedDurationMs,
			fullyAppliedDurationMs,
			acknowledgementLagMs:
				!request.invalid && completions.length ? request.acknowledgementLagMs : undefined,
		});
	}

	dispose(): void {
		for (const id of this.requests.keys()) {
			this.finish(id, 'cancelled');
		}
		this.targets.clear();
		this.doc?.removeEventListener('visibilitychange', this.observeVisibility);
		this.observing = false;
		this.visibility = [];
	}
}
